import { useQuery } from "@tanstack/react-query";
import { Activity, AlertCircle } from "lucide-react";
import { formatDistanceToNowStrict, subHours } from "date-fns";
import { fr } from "date-fns/locale";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Sprint 10.x — Signaux à surveiller : erreurs agent IA des dernières 24h.
// Source : table skill_runs (status='error'). Tri par fréquence (skill_id).
// Pas via dataProvider car skill_runs n'est pas une Resource ra-core registrée.

type SkillRunErrorRow = {
  id: number;
  skill_id: string;
  error_code: string | null;
  error_message: string | null;
  started_at: string;
};

export function SignauxCard() {
  const since24h = subHours(new Date(), 24).toISOString();
  const { data, isPending } = useQuery<SkillRunErrorRow[]>({
    queryKey: ["signaux_card", since24h],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("skill_runs")
        .select("id,skill_id,error_code,error_message,started_at")
        .eq("status", "error")
        .gte("started_at", since24h)
        .order("started_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as SkillRunErrorRow[];
    },
    staleTime: 30_000,
  });

  const errors = data ?? [];
  const bySkill = new Map<
    string,
    { count: number; last: SkillRunErrorRow; lastCode: string | null }
  >();
  for (const e of errors) {
    const cur = bySkill.get(e.skill_id);
    if (cur) {
      cur.count += 1;
    } else {
      bySkill.set(e.skill_id, {
        count: 1,
        last: e,
        lastCode: e.error_code,
      });
    }
  }
  const grouped = Array.from(bySkill.entries())
    .map(([skill_id, v]) => ({ skill_id, ...v }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Activity className="size-4 text-fabrik-text-3" />
          <h2 className="text-[14px] font-semibold text-foreground">
            Signaux à surveiller
          </h2>
          <span className="ml-auto text-[11.5px] text-fabrik-text-3">24h</span>
        </div>
        {isPending ? (
          <div className="p-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ) : grouped.length === 0 ? (
          <p className="text-[12.5px] text-fabrik-text-3 px-4 py-4 leading-relaxed">
            Aucune erreur agent dans les dernières 24h. Tout roule.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {grouped.map((g) => (
              <li key={g.skill_id} className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <AlertCircle className="size-3.5 text-fabrik-red shrink-0" />
                  <span className="text-[13px] font-medium text-foreground truncate">
                    {g.skill_id}
                  </span>
                  <span className="ml-auto text-[11.5px] text-fabrik-red font-semibold tabular-nums">
                    ×{g.count}
                  </span>
                </div>
                {(g.lastCode || g.last.error_message) && (
                  <p className="text-[11.5px] text-fabrik-text-3 mt-0.5 truncate">
                    {g.lastCode ?? ""}
                    {g.lastCode && g.last.error_message ? " · " : ""}
                    {g.last.error_message?.slice(0, 100) ?? ""}
                  </p>
                )}
                <p className="text-[10.5px] text-fabrik-text-3 mt-0.5">
                  Dernière :{" "}
                  {formatDistanceToNowStrict(new Date(g.last.started_at), {
                    locale: fr,
                    addSuffix: true,
                  })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
